import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TenantFormData, INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS, TENANT_TYPE_OPTIONS } from "@/types/tenant";
import { Building2, Users } from "lucide-react";

interface BusinessInfoStepProps {
  formData: TenantFormData;
  updateFormData: (updates: Partial<TenantFormData>) => void;
  errors: Record<string, string>;
}

export function BusinessInfoStep({ formData, updateFormData, errors }: BusinessInfoStepProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Business Information</h2>
        <p className="text-muted-foreground">
          Tell us about the company you're onboarding
        </p>
      </div>

      <div className="grid gap-4">
        {/* Tenant Type Selection */}
        <div className="space-y-3">
          <Label>Tenant Type *</Label>
          <div className="grid grid-cols-2 gap-3">
            {TENANT_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => updateFormData({ tenant_type: option.value })}
                className={`flex flex-col items-start gap-1 rounded-lg border-2 p-4 text-left transition-all ${
                  formData.tenant_type === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  {option.value === 'customer' ? (
                    <Building2 className="h-4 w-4 text-primary" />
                  ) : (
                    <Users className="h-4 w-4 text-primary" />
                  )}
                  <span className="font-medium">{option.label}</span>
                </div>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Company Name *</Label>
          <Input
            id="name"
            placeholder="Enter company name"
            value={formData.name}
            onChange={(e) => updateFormData({ name: e.target.value })}
            className={errors.name ? "border-destructive" : ""}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Select
              value={formData.industry}
              onValueChange={(value) => updateFormData({ industry: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_size">Company Size</Label>
            <Select
              value={formData.company_size}
              onValueChange={(value) => updateFormData({ company_size: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_SIZE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="expected_device_count">Expected Device Count</Label>
          <Input
            id="expected_device_count"
            type="number"
            placeholder="Estimated number of devices to enroll"
            value={formData.expected_device_count || ""}
            onChange={(e) => updateFormData({ 
              expected_device_count: e.target.value ? parseInt(e.target.value) : undefined 
            })}
            min={0}
          />
          <p className="text-xs text-muted-foreground">
            This helps us prepare the right resources for your customer
          </p>
        </div>

        {/* Parent Management Toggle */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="can_parent_manage" className="text-sm font-medium">
              Allow Parent Management
            </Label>
            <p className="text-xs text-muted-foreground">
              When enabled, you (the parent tenant) can manage this tenant's configurations and devices
            </p>
          </div>
          <Switch
            id="can_parent_manage"
            checked={formData.can_parent_manage}
            onCheckedChange={(checked) => updateFormData({ can_parent_manage: checked })}
          />
        </div>
      </div>
    </div>
  );
}
